import { Empty, Tag, Typography } from "antd"
import dayjs from "dayjs"
import { useAtomValue } from "jotai"
import { groupBy } from "lodash"
import { useMemo, useState } from "react"

import { studentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { SFormHeader } from "#blocks/Form/formComponents"
import EvaluationFormModal from "#components/views/EvaluationView/EvaluationFormModal"
import { EvaluationFormContextProvider } from "#components/views/EvaluationView/useEvaluationFormContext"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import {
  EVAL_COLORS,
  EvaluationConstructor,
} from "#customTypes/composedTypes/evaluationTypes"
import { Uuid } from "#customTypes/globalTypes"
import useUserEvaluations from "#hooks/dataHooks/useUserEvaluations"
import { formatSemester } from "#utils/semesterHelpers"
import { camelToWords } from "#utils/textFormatters"

const { Text } = Typography

interface EvalRowProps {
  evaluation: EvaluationConstructor
  onClick: () => void
}

const EvalRow = ({ evaluation, onClick }: EvalRowProps) => {
  const student = useAtomValue(studentAtoms.docByIdAtom(evaluation.studentId))
  const theme = useThemeContext()

  const statusColor = EVAL_COLORS[evaluation.status]
  const statusLabel = camelToWords(evaluation.status)

  const dateInfo = useMemo(() => {
    const dates = [
      { label: "Printed", date: evaluation.printedAt },
      { label: "Approved", date: evaluation.approvedAt },
      { label: "Submitted", date: evaluation.submittedAt },
      { label: "Saved", date: evaluation.savedAt },
      { label: "Created", date: evaluation.createdAt },
    ].filter((d) => d.date)

    if (!dates.length) return null
    const mostRecent = dates[0]
    return `${mostRecent.label}: ${dayjs(mostRecent.date).format("MMM D, YYYY")}`
  }, [
    evaluation.printedAt,
    evaluation.approvedAt,
    evaluation.submittedAt,
    evaluation.savedAt,
    evaluation.createdAt,
  ])

  return (
    <Flex
      onClick={onClick}
      style={{
        padding: "6px 10px",
        borderRadius: 4,
        background: theme.body2,
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        transition: "background 0.2s",
      }}
      className="eval-row-hover"
    >
      <Flex col gap={2}>
        <Text strong style={{ fontSize: 13 }}>
          {student?.name ?? "Unknown Student"}
        </Text>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {camelToWords(evaluation.classGroup)}
        </Text>
      </Flex>
      <Flex gap={8} style={{ alignItems: "center" }}>
        {dateInfo && (
          <Text type="secondary" style={{ fontSize: 10 }}>
            {dateInfo}
          </Text>
        )}
        <Tag color={statusColor} style={{ margin: 0 }}>
          {statusLabel}
        </Tag>
      </Flex>
    </Flex>
  )
}

interface EvalSectionProps {
  title: string
  evals: EvaluationConstructor[]
  emptyText?: string
  onClickEval: (id: Uuid) => void
}

const EvalSection = ({
  title,
  evals,
  emptyText = "None",
  onClickEval,
}: EvalSectionProps) => {
  const evalsBySemester = useMemo(() => {
    const grouped = groupBy(evals, "semester")
    // Sort semesters in reverse order (most recent first)
    const sortedSemesters = Object.keys(grouped).sort().reverse()
    return sortedSemesters.map((semester) => ({
      semester,
      evals: grouped[semester],
    }))
  }, [evals])

  return (
    <Flex col gap={8}>
      <Flex
        style={{
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Text strong style={{ fontSize: 14 }}>
          {title}
        </Text>
        <Tag style={{ margin: 0 }}>{evals.length}</Tag>
      </Flex>
      {evals.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {emptyText}
        </Text>
      ) : (
        <Flex col gap={8}>
          {evalsBySemester.map(({ semester, evals: semesterEvals }) => (
            <Flex
              col
              gap={4}
              key={`evals-section-${semester}`}
              style={{ marginBottom: 20 }}
            >
              <Text
                type="secondary"
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                {formatSemester(semester)}
              </Text>
              {semesterEvals.map((ev) => (
                <EvalRow
                  key={ev.id}
                  evaluation={ev}
                  onClick={() => onClickEval(ev.id)}
                />
              ))}
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  )
}

interface UserEvaluationsListProps {
  userId: Uuid | undefined
}

const UserEvaluationsList = ({ userId }: UserEvaluationsListProps) => {
  const [selectedEvalId, setSelectedEvalId] = useState<Uuid | null>(null)

  const { authoredEvals, approvedByEvals, printedByEvals } =
    useUserEvaluations(userId)

  const totalCount =
    authoredEvals.length + approvedByEvals.length + printedByEvals.length

  const handleClickEval = (evalId: Uuid) => {
    setSelectedEvalId(evalId)
  }

  const handleCloseModal = () => {
    setSelectedEvalId(null)
  }

  if (!userId) {
    return (
      <Flex col>
        <SFormHeader>Evaluations</SFormHeader>
        <Empty
          description="Save user first to see evaluations"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Flex>
    )
  }

  return (
    <>
      <Flex col gap={16} style={{ height: "100%" }}>
        <Flex style={{ justifyContent: "space-between", alignItems: "center" }}>
          <SFormHeader style={{ margin: 0 }}>Evaluations</SFormHeader>
          <Tag>{totalCount} total</Tag>
        </Flex>

        <Flex
          col
          gap={16}
          style={{ flex: 1, overflowY: "auto", paddingRight: 20 }}
        >
          <EvalSection
            title="Written"
            evals={authoredEvals}
            emptyText="No evaluations assigned, saved, or submitted"
            onClickEval={handleClickEval}
          />

          <EvalSection
            title="Approved"
            evals={approvedByEvals}
            emptyText="No approvals"
            onClickEval={handleClickEval}
          />

          <EvalSection
            title="Printed"
            evals={printedByEvals}
            emptyText="No prints"
            onClickEval={handleClickEval}
          />
        </Flex>
      </Flex>

      {selectedEvalId && (
        <EvaluationFormContextProvider>
          <EvaluationFormModal
            evaluationId={selectedEvalId}
            onClose={handleCloseModal}
          />
        </EvaluationFormContextProvider>
      )}
    </>
  )
}

export default UserEvaluationsList
